import type { CreateUserInput, UpdateUserInput, UserDto } from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export interface UserListParams {
  q?: string;
  page?: number;
  perPage?: number;
  includeInactive?: boolean;
}

export const usersQueryOptions = (params: UserListParams) =>
  queryOptions({
    queryKey: queryKeys.users.list(params),
    queryFn: ({ signal }) => api.list<UserDto>('/users', { ...params }, signal),
    placeholderData: (previous) => previous,
  });

/** `enabled` lets closed dialogs mount without fetching the user list. */
export const useUsers = (params: UserListParams = {}, options: { enabled?: boolean } = {}) =>
  useQuery({ ...usersQueryOptions(params), enabled: options.enabled ?? true });

const useUserInvalidation = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
  };
};

export const useCreateUser = () => {
  const invalidate = useUserInvalidation();
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post<UserDto>('/users', input),
    onSuccess: invalidate,
  });
};

export const useUpdateUser = () => {
  const invalidate = useUserInvalidation();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: UpdateUserInput }) =>
      api.patch<UserDto>(`/users/${userId}`, input),
    onSuccess: invalidate,
  });
};

export const useDeleteUser = () => {
  const invalidate = useUserInvalidation();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/users/${userId}`),
    onSuccess: invalidate,
  });
};
